import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import { IMG, resolveImage } from "../data/content";

const SPECIES = [
  { label: "Mammals", text: "Tiger, leopard, jungle cat, Asian elephant, cheetal, sambhar, hog deer, barking deer, wild boar, langur, yellow-throated marten, porcupine, sloth bear, rhesus macaque and jackal." },
  { label: "Birds", text: "Peafowl, jungle fowl, kaleej pheasant, parakeets, laughing thrushes, orioles, kingfishers, drongos, woodpeckers, hornbills, vultures, Pallas's fish eagle, tawny fish owl, Indian pitta, scarlet minivet and collared falconet — among hundreds more." },
  { label: "Reptiles & fish", text: "Marsh crocodile, gharial, king cobra, common krait, Russell's viper, python and monitor lizard — and in the river, mahseer and goonch." },
];

const RULES = [
  "Walking or trekking inside the national park itself is strictly prohibited.",
  "Wear colours that merge with the forest; avoid perfumes and bright clothes.",
  "Carry a litter bag; bring back everything non-biodegradable.",
  "Move quietly. Do not smoke, pluck plants, or collect anything.",
  "Never use flash on wild animals; never tease, chase or feed them.",
  "Respect local traditions, customs and people.",
];

export default function WildlifePage() {
  return (
    <main data-testid="wildlife-page">
      <Seo
        title="Wildlife in Corbett | The Jamoon"
        description="Explore the wildlife and biodiversity around The Jamoon in Corbett, from big cats and elephants to birds, reptiles and the forests of the Ramganga landscape."
        path="/wildlife"
        image={resolveImage("tigerNight", 1920, 1080)}
        imageAlt="Tiger photographed at night near The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Wildlife", path: "/wildlife" }]}
      />
      <PageHero
        testid="wildlife-hero"
        image={resolveImage("tigerNight", 1920, 1080)}
        imageAlt="Tiger photographed at night near The Jamoon in Corbett"
        eyebrow="Wildlife"
        title={<>Wildlife Around <span className="italic text-brass">The Jamoon</span></>}
        sub="Wildlife here is not a scheduled performance. It is a possibility — and that is worth far more."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Corbett Tiger Reserve</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-4xl">
                Asia's first <span className="italic text-brass">national park.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/75">
                Established in 1936 as Hailey National Park to protect the endangered Bengal
                tiger, Corbett was the first national park in mainland Asia. It was later renamed
                for James Edward Corbett — the hunter-turned-conservationist whose footsteps give
                this lodge its purpose — and came under Project Tiger in 1971.
              </p>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-ivory/55 sm:text-base">
                The reserve spans over 520 square kilometres of hills, riverine belts, grasslands
                and sal forest. Nearly three-quarters of it is forest, holding around 102 species
                of mammals, 623 species of birds, 124 species of fish and 69 species of reptiles.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="group relative overflow-hidden">
            <img
              src={resolveImage("leopardRocks", 900, 1100)}
              alt="A leopard crosses the rocks at night — camera trap at The Jamoon"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
            <p className="absolute bottom-5 left-5 text-[11px] tracking-[0.25em] text-ivory/80">
              CAMERA TRAP · THE JAMOON
            </p>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-8 md:grid-cols-3">
          {SPECIES.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div data-testid={`species-card-${i}`} className="border border-brass/20 p-8">
                <h3 className="font-serif text-2xl font-light text-brass">{s.label}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ivory/60">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-brass/15 bg-forest px-6 py-24 sm:px-12 sm:py-28">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">In the Forest</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivory sm:text-4xl">
              The house rules <span className="italic text-brass">of the wild.</span>
            </h2>
          </Reveal>
          <div className="mt-10">
            {RULES.map((r, i) => (
              <Reveal key={r} delay={i * 0.05}>
                <p data-testid={`wildlife-rule-${i}`} className="border-t border-ivory/10 py-4 text-sm leading-relaxed text-ivory/70 last:border-b sm:text-base">
                  <span className="mr-4 font-serif italic text-brass/70">{String(i + 1).padStart(2, "0")}</span>
                  {r}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCta />
    </main>
  );
}
